import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { YearProvider } from './contexts/YearContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import Auth from './components/Auth';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import FinancialPlanSetup from './components/FinancialPlanSetup';
import Calculator from './components/Calculator';
import WeeklyTracker from './components/WeeklyTracker';
import MonthlyChecks from './components/MonthlyChecks';
import AccountBalances from './components/AccountBalances';
import Reports from './components/Reports';
import BusinessOverview from './components/BusinessOverview';
import ExpenseTracker from './components/ExpenseTracker';
import AllocationPreview from './components/AllocationPreview';
import TeamDashboard from './components/TeamDashboard';
import TeamMemberView from './components/TeamMemberView';
import TeamAdmin from './components/TeamAdmin';
import Settings from './components/Settings';
import SettingsDropdown from './components/SettingsDropdown';
import FinAI from './components/FinAI';
import WhatsNewModal, { hasSeenWhatsNew } from './components/WhatsNewModal';
import SyncStatusPill from './components/SyncStatusPill';
import AdminReconcile from './components/AdminReconcile';
import CashflowSettings from './components/CashflowSettings';
import { Toaster } from '@/components/ui/sonner';
import { DollarSign, Calculator as CalcIcon, Table, Calendar, PieChart, FileText, Home, TrendingDown, TrendingUp, LogOut, Loader2, Users, Wallet } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  
  // Base navigation items
  const baseNavItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/setup', label: 'Financial Plan', icon: FileText },
    { path: '/calculator', label: 'Weekly Income', icon: CalcIcon },
    { path: '/expenses', label: 'Track Expenses', icon: TrendingDown },
    { path: '/tracker', label: '52-Week Tracker', icon: Table },
    { path: '/monthly', label: 'Monthly Checks', icon: Calendar },
    { path: '/accounts', label: 'Accounts', icon: DollarSign },
    { path: '/reports', label: 'Reports', icon: PieChart },
    { path: '/business-overview', label: 'Business Overview', icon: TrendingUp },
    { path: '/cashflow/settings', label: 'Cashflow Settings', icon: Wallet },
  ];
  
  // Clean navigation items - moved Team Admin and Settings to Settings dropdown
  let navItems = [...baseNavItems];
  
  // Only add "My Team" to main navigation for leaders
  if (user?.team_mode_enabled && ['satellite', 'fsl', 'fta'].includes(user?.role)) {
    navItems.unshift({ path: '/team', label: 'My Team', icon: Users });
  }

  return (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-800 dark:from-gray-800 dark:to-gray-900 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-28">
          <div className="flex items-center">
            <Link to="/" className="cursor-pointer hover:opacity-80 transition-opacity" title="Go to Dashboard">
              <img src="/logo.png" alt="Paper Trail Logo" className="h-12 w-auto lg:h-24" />
            </Link>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Settings Dropdown - replaces user info and logout */}
            <SettingsDropdown />
            
            {/* Mobile menu button - visible on screens smaller than md */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md hover:bg-blue-700 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Desktop navigation - visible on md screens and above */}
        <div className="hidden md:block pb-3" data-testid="desktop-nav">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-white text-blue-800 shadow-md'
                      : 'text-white hover:bg-blue-700 dark:hover:bg-gray-700'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
        
        {/* Mobile navigation dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-white text-blue-800 shadow-md'
                      : 'text-white hover:bg-blue-700'
                  }`}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-base font-medium">{item.label}</span>
                </Link>
              );
            })}
            {/* Mobile logout button */}
            <button
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white hover:bg-blue-700 transition-all"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-base font-medium">Logout</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
}

// Gate that auto-opens the What's New walkthrough once per version per user.
// Server-side `last_seen_whats_new_version` ensures it persists across devices,
// with localStorage as a secondary guard. Reopenable from a custom window event.
function WhatsNewGate() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    if (!hasSeenWhatsNew(user)) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('papertrail:show-whats-new', handler);
    return () => window.removeEventListener('papertrail:show-whats-new', handler);
  }, []);

  return <WhatsNewModal open={open} onClose={() => setOpen(false)} />;
}

// Main App Routes Component
function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Public routes that don't require authentication
  const publicRoutes = ['/auth', '/forgot-password', '/reset-password'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // If not authenticated and trying to access protected route
  if (!isAuthenticated && !isPublicRoute) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Auth />} />
      </Routes>
    );
  }

  // If authenticated and on auth page, redirect to dashboard
  if (isAuthenticated && (location.pathname === '/auth' || location.pathname === '/forgot-password')) {
    return <Navigate to="/" replace />;
  }

  // Public routes (forgot password, reset password)
  if (isPublicRoute && !isAuthenticated) {
    return (
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  // Protected routes (authenticated users)
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <Navigation />
      <WhatsNewGate />
      {/* Persistent sync-status pill — floats in the top-right corner below the
          nav so it's visible on every authenticated route while a balance
          mutation is in-flight. Hidden when idle. */}
      <div className="fixed top-20 right-4 z-40 pointer-events-none" data-testid="sync-status-pill-anchor">
        <SyncStatusPill />
      </div>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/team-admin" element={<TeamAdmin />} />
          <Route path="/team" element={<TeamDashboard />} />
          <Route path="/team/member/:memberId" element={<TeamMemberView />} />
          <Route path="/setup" element={<FinancialPlanSetup />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/expenses" element={<ExpenseTracker />} />
          <Route path="/tracker" element={<WeeklyTracker />} />
          <Route path="/monthly" element={<MonthlyChecks />} />
          <Route path="/accounts" element={<AccountBalances />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/business-overview" element={<BusinessOverview />} />
          <Route path="/allocation-preview" element={<AllocationPreview />} />
          <Route path="/admin/reconcile" element={<AdminReconcile />} />
          <Route path="/cashflow/settings" element={<CashflowSettings />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {/* Fin AI - Floating Chat Assistant */}
      <FinAI />
    </div>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <BrowserRouter>
        <AuthProvider>
          <YearProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </YearProvider>
        </AuthProvider>
      </BrowserRouter>
    </DarkModeProvider>
  );
}

export default App;
export { API };
